import {ApplicationRef, Injectable} from "@angular/core";
import {Observable, Subject, fromEvent, debounceTime} from "rxjs";
import {FUIBreakpoint} from "./breakpoint.model";

@Injectable()
export class FUIResponsiveService {
    public isIos: boolean;
    public isIpad: boolean;

    private _loaded: Subject<Window> = new Subject<Window>();
    public loaded$: Observable<Window> = this._loaded.asObservable();
    private _window: Subject<Window> = new Subject<Window>();
    public window$: Observable<Window> = this._window.asObservable();

    // fires only when the width has changed
    private _windowWidth: Subject<Window> = new Subject<Window>();
    public windowWidth$: Observable<Window> = this._windowWidth.asObservable();

    // fires only when the height has changed
    private _windowHeight: Subject<Window> = new Subject<Window>();
    public windowHeight$: Observable<Window> = this._windowHeight.asObservable();

    private lastInnerWidth: number;
    private lastInnerHeight: number;

    constructor(private applicationRef: ApplicationRef) {
        this.isIpad = (/iPad/.test(navigator.userAgent) && !window["MSStream"]) || (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
        this.isIos = (/iPhone|iPod/.test(navigator.userAgent) && !window["MSStream"]) || this.isIpad;

        this.lastInnerWidth = this.host().innerWidth;
        this.lastInnerHeight = this.host().innerHeight;

        /**
         * Watch the window for resize and update with
         * a new window instance
         */
        fromEvent(window, "resize")
            .pipe(debounceTime(500))
            .subscribe(
                () => {
                    this._window.next(window);
                    if (this.lastInnerWidth !== this.host().innerWidth) {
                        this.lastInnerWidth = this.host().innerWidth;
                        this.emitWindowWidthChange();
                    }
                    if (this.lastInnerHeight !== this.host().innerHeight) {
                        this.lastInnerHeight = this.host().innerHeight;
                        this.emitWindowHeightChange();
                    }

                    /**
                     * We have to tell the application something has changed
                     * because this is not part of the change detection that
                     * Angular runs
                     */
                    this.applicationRef.tick();
                }
            );

        /**
         * Watch the window for load
         */
        fromEvent(window, "load")
            .pipe(debounceTime(1))
            .subscribe(
                () => {
                    this._loaded.next(window);

                    /**
                     * We have to tell the application something has changed
                     * because this is not part of the change detection that
                     * Angular runs
                     */
                    this.applicationRef.tick();
                }
            );
    }

    maxHeight(element: HTMLElement): number {
        return this.host().innerHeight - element.getBoundingClientRect().top - 16;
    }

    /**
     * Used to determine the left offset and whether an element is on
     * or off screen horizontally
     * @param element
     * @returns number;
     */
    offset(element: HTMLElement): number {
        if (element.getBoundingClientRect().left + element.getBoundingClientRect().width > this.host().innerWidth) {
            const result = (this.host().innerWidth - element.getBoundingClientRect().right);

            return -Math.abs(result - 16);
        }
        else {
            return 0;
        }
    }

    /**
     *
     * @param element
     * @returns {number}
     */
    overflow(element: HTMLElement): number {
        return this.host().innerHeight - element.getBoundingClientRect().bottom - element.getBoundingClientRect().height;
    }

    /**
     *
     * @returns {Window}
     */
    host(): Window {
        return window;
    }

    emitWindowChange(): void {
        this._window.next(window);
    }

    emitWindowWidthChange(): void {
        this._windowWidth.next(window);
    }

    emitWindowHeightChange(): void {
        this._windowHeight.next(window);
    }

    breakpointApplies(breakpoint: FUIBreakpoint): boolean {
        return breakpoint.appliesAt(this.lastInnerWidth, this.lastInnerHeight);
    }
}
